import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { CaptionsTranscript, TranscriptEntry } from '@osmosis/shared/video';

export type TranscriptChapter = CaptionsTranscript['chapters'][number];

/** A chapter whose entries carry search-highlighted markup. */
export type HighlightedChapter = {
  chapter: TranscriptChapter;
  entries: { entry: TranscriptEntry; html: string }[];
};

/** Stable DOM id / React key for a chapter section by index. */
export const chapterDomId = (index: number) => `captions-chapter-${index}`;

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const MARK_BASE_CLASS = 'rounded-sm bg-yellow-300';
const MARK_CURRENT_CLASS = `rounded-sm ${text0} bg-orange-500 ring-2 ring-orange-500 ring-offset-1`;
// Capturing group is required — String.split() only keeps the matched
// delimiter in the output when the pattern has one; without it, every tag
// would be silently dropped from the result instead of preserved as its own
// segment.
const HTML_TAG_RE = /(<[^>]+>)/g;

/**
 * Wraps each case-insensitive match of `query` in a <mark>, skipping HTML tag
 * segments so markup never breaks. Highlight classes — including the ring on
 * the current match — are baked into the markup so React owns them and can't
 * have them wiped out by an unrelated re-render. `startIndex` is this entry's
 * offset into the transcript-wide match sequence; `currentIndex` is global.
 * Returns the markup plus its match count, so callers can thread a running
 * index across entries.
 */
export const highlightHtml = (
  html: string,
  query: string,
  startIndex: number,
  currentIndex: number,
): { html: string; count: number } => {
  if (!query) return { html, count: 0 };
  const re = new RegExp(`(${escapeRegExp(query)})`, 'gi');
  let count = 0;
  const highlighted = html
    .split(HTML_TAG_RE)
    .map((segment) =>
      segment.startsWith('<')
        ? segment
        : segment.replace(re, (match) => {
            const isCurrent = startIndex + count === currentIndex;
            count += 1;
            const cls = isCurrent ? MARK_CURRENT_CLASS : MARK_BASE_CLASS;
            return `<mark class="${cls}">${match}</mark>`;
          }),
    )
    .join('');
  return { html: highlighted, count };
};
